import { RefObject, useLayoutEffect, useState } from 'react';

type Align = 'center' | 'left' | 'right';

/**
 * Positions a header dropdown a fixed distance below the bottom of the header,
 * horizontally anchored to its toggle.
 *
 * The popup is `position: fixed`, so its containing block is the header
 * (`rep-header-container` establishes one via its `transform`). `top` is then
 * `calc(100% + 5px)` — 5px below the header's bottom edge — which adapts
 * automatically to the header's height (mobile 70px / desktop 110px) instead of
 * relying on each toggle's height. The horizontal position is measured from the
 * toggle so the popup still tracks its icon.
 *
 * The popup is hidden until the first measurement lands so it never paints at a
 * stale position and snaps into place.
 */
export function useHeaderPopupAnchor(
  toggleRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
  align: Align = 'center',
  // Horizontal nudge (px) applied to the measured anchor edge.
  offsetX = 0,
) {
  const [pos, setPos] = useState<{ left?: number; right?: number } | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPos(null);
      return;
    }
    const measure = () => {
      const el = toggleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (align === 'center') {
        setPos({ left: rect.left + rect.width / 2 + offsetX });
      } else if (align === 'left') {
        setPos({ left: rect.left + offsetX });
      } else {
        setPos({ right: window.innerWidth - rect.right + offsetX });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('orientationchange', measure);
    };
  }, [isOpen, toggleRef, align, offsetX]);

  return {
    position: 'fixed' as const,
    top: 'calc(100% + 10px)',
    ...(pos?.left != null ? { left: `${pos.left}px` } : {}),
    ...(pos?.right != null ? { right: `${pos.right}px` } : {}),
    ...(align === 'center' ? { transform: 'translateX(-50%)' } : {}),
    visibility: pos != null ? ('visible' as const) : ('hidden' as const),
  };
}
